import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSchool } from '../context/SchoolContext';
import {
  DollarSign, Plus, FileText, Download, TrendingUp,
  Clock, CheckCircle, AlertTriangle, Trash2, Loader2,
  Receipt, Settings, CalendarDays, Building2, Car,
  CircleDollarSign, Percent, ArrowRight, Camera, X, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'shifts', label: 'Shifts' },
  { key: 'payments', label: 'Payments' },
  { key: 'expenses', label: 'Expenses' },
  { key: 'tax', label: 'Tax' },
  { key: 'reports', label: 'Reports' },
  { key: 'settings', label: 'Settings' },
];

const PAY_SOURCES = ['Department', 'Agency', 'Other'];
const SESSION_TYPES = ['Full day', 'Half day', 'Hourly'];
const EXPENSE_CATEGORIES = ['Travel (km)', 'Parking', 'Tolls', 'Teaching resources', 'Subscriptions', 'Equipment', 'Other'];

const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    overdue: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${styles[status] || styles.pending}`} data-testid={`status-${status}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const fmt = (n) => `$${(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const EarningsPage = () => {
  const { selectedSchool } = useSchool();
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schools, setSchools] = useState([]);

  // Dialogs
  const [shiftDialog, setShiftDialog] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [expenseDialog, setExpenseDialog] = useState(false);

  // Forms
  const [shiftForm, setShiftForm] = useState({ school_name: '', date: new Date().toISOString().split('T')[0], pay_source: 'Department', session_type: 'Full day', rate: '', hours: '', notes: '' });
  const [payForm, setPayForm] = useState({ amount: '', pay_source: 'Department', payment_date: new Date().toISOString().split('T')[0], shift_ids: [], notes: '' });
  const [expForm, setExpForm] = useState({ date: new Date().toISOString().split('T')[0], category: 'Travel (km)', amount: '', notes: '', receipt_base64: '' });
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exportType, setExportType] = useState('both');
  const receiptRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [dRes, shRes, paRes, exRes, seRes, scRes] = await Promise.all([
        fetch(`${API}/earnings/dashboard`, { credentials: 'include' }),
        fetch(`${API}/earnings/shifts`, { credentials: 'include' }),
        fetch(`${API}/earnings/payments`, { credentials: 'include' }),
        fetch(`${API}/earnings/expenses`, { credentials: 'include' }),
        fetch(`${API}/earnings/settings`, { credentials: 'include' }),
        fetch(`${API}/schools`, { credentials: 'include' }),
      ]);
      if (dRes.ok) setDash(await dRes.json());
      if (shRes.ok) setShifts(await shRes.json());
      if (paRes.ok) setPayments(await paRes.json());
      if (exRes.ok) setExpenses(await exRes.json());
      if (seRes.ok) {
        const s = await seRes.json();
        setSettings(s);
        setSettingsForm(s);
        setShiftForm(prev => ({ ...prev, rate: s.default_full_day_rate || '' }));
      }
      if (scRes.ok) setSchools(await scRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-calculate expected pay
  const calcExpectedPay = () => {
    const rate = parseFloat(shiftForm.rate) || 0;
    if (shiftForm.session_type === 'Full day') return rate;
    if (shiftForm.session_type === 'Half day') return rate;
    if (shiftForm.session_type === 'Hourly') return rate * (parseFloat(shiftForm.hours) || 0);
    return rate;
  };

  // Auto-fill rate when session type changes
  const handleSessionTypeChange = (type) => {
    let rate = '';
    if (settings) {
      if (type === 'Full day') rate = settings.default_full_day_rate || '';
      if (type === 'Half day') rate = settings.default_half_day_rate || '';
      if (type === 'Hourly') rate = settings.default_hourly_rate || '';
    }
    setShiftForm(prev => ({ ...prev, session_type: type, rate }));
  };

  const handleAddShift = async () => {
    const expected_pay = calcExpectedPay();
    if (!shiftForm.school_name || !shiftForm.rate) { toast.error('Fill required fields'); return; }
    try {
      const r = await fetch(`${API}/earnings/shifts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...shiftForm, rate: parseFloat(shiftForm.rate), hours: shiftForm.hours ? parseFloat(shiftForm.hours) : null, expected_pay }),
      });
      if (r.ok) {
        toast.success('Shift added');
        setShiftDialog(false);
        setShiftForm({ school_name: '', date: new Date().toISOString().split('T')[0], pay_source: 'Department', session_type: 'Full day', rate: settings?.default_full_day_rate || '', hours: '', notes: '' });
        fetchAll();
      }
    } catch (e) { toast.error('Failed'); }
  };

  const handleMarkPaid = async (shiftId) => {
    await fetch(`${API}/earnings/shifts/${shiftId}/paid`, { method: 'PUT', credentials: 'include' });
    toast.success('Marked as paid');
    fetchAll();
  };

  const handleDeleteShift = async (shiftId) => {
    await fetch(`${API}/earnings/shifts/${shiftId}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Shift removed');
    fetchAll();
  };

  const handleRecordPayment = async () => {
    if (!payForm.amount) { toast.error('Enter amount'); return; }
    try {
      const r = await fetch(`${API}/earnings/payments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...payForm, amount: parseFloat(payForm.amount) }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.discrepancy !== 0) toast.warning(`Discrepancy: ${fmt(data.discrepancy)}`);
        else toast.success('Payment recorded');
        setPayDialog(false);
        setPayForm({ amount: '', pay_source: 'Department', payment_date: new Date().toISOString().split('T')[0], shift_ids: [], notes: '' });
        fetchAll();
      }
    } catch (e) { toast.error('Failed'); }
  };

  const handleAddExpense = async () => {
    if (!expForm.amount) { toast.error('Enter amount'); return; }
    try {
      const body = { ...expForm, amount: parseFloat(expForm.amount) };
      if (!body.receipt_base64) delete body.receipt_base64;
      const r = await fetch(`${API}/earnings/expenses`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success('Expense added');
        setExpenseDialog(false);
        setExpForm({ date: new Date().toISOString().split('T')[0], category: 'Travel (km)', amount: '', notes: '', receipt_base64: '' });
        setReceiptPreview(null);
        fetchAll();
      }
    } catch (e) { toast.error('Failed'); }
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setReceiptPreview(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onload = () => {
      setExpForm(p => ({ ...p, receipt_base64: reader.result.split(',')[1] }));
    };
    reader.readAsDataURL(file);
    if (receiptRef.current) receiptRef.current.value = '';
  };

  const clearReceipt = () => {
    setReceiptPreview(null);
    setExpForm(p => ({ ...p, receipt_base64: '' }));
  };

  const handleSaveSettings = async () => {
    try {
      const r = await fetch(`${API}/earnings/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(settingsForm),
      });
      if (r.ok) { toast.success('Settings saved'); fetchAll(); }
    } catch (e) { toast.error('Failed'); }
  };

  const handleExportCSV = async () => {
    const params = new URLSearchParams();
    if (exportFrom) params.set('date_from', exportFrom);
    if (exportTo) params.set('date_to', exportTo);
    window.open(`${API}/earnings/export?${params.toString()}`, '_blank');
    toast.success('Downloading CSV');
  };

  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      doc.setFontSize(18);
      const title = exportType === 'pay' ? 'Pay Report' : exportType === 'expenses' ? 'Expenses Report' : 'Earnings Report';
      doc.text(title, 14, 20);
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
      let y = 38;
      doc.setFontSize(12);
      doc.text('Summary', 14, y); y += 8;
      doc.setFontSize(10);
      if (dash) {
        if (exportType !== 'expenses') {
          doc.text(`This Month: ${fmt(dash.month_earned)}`, 14, y); y += 6;
          doc.text(`Financial Year: ${fmt(dash.fy_earned)}`, 14, y); y += 6;
          doc.text(`Total Paid: ${fmt(dash.total_paid)}`, 14, y); y += 6;
          doc.text(`Outstanding: ${fmt(dash.total_unpaid)}`, 14, y); y += 6;
        }
        if (exportType !== 'pay') {
          doc.text(`Expenses (FY): ${fmt(dash.fy_expenses)}`, 14, y); y += 6;
        }
        if (exportType === 'both') {
          doc.text(`Est. Tax: ${fmt(dash.estimated_tax)}`, 14, y); y += 6;
          doc.text(`Net Income: ${fmt(dash.net_income)}`, 14, y); y += 6;
        }
        y += 6;
      }
      if (exportType !== 'expenses') {
        doc.setFontSize(12);
        doc.text('Shifts', 14, y); y += 8;
        doc.setFontSize(9);
        shifts.forEach(s => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`${s.date} | ${s.school_name} | ${fmt(s.expected_pay)} | ${s.status}`, 14, y);
          y += 5;
        });
        y += 8;
      }
      if (exportType !== 'pay') {
        doc.setFontSize(12);
        doc.text('Expenses', 14, y); y += 8;
        doc.setFontSize(9);
        expenses.forEach(e => {
          if (y > 270) { doc.addPage(); y = 20; }
          doc.text(`${e.date} | ${e.category} | ${fmt(e.amount)}`, 14, y);
          y += 5;
        });
      }
      doc.save(`${title.toLowerCase().replace(/\s/g, '_')}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) { toast.error('PDF failed'); }
  };

  const unpaidShifts = shifts.filter(s => s.status !== 'paid');

  if (loading) {
    return (
      <Layout breadcrumb={[{ label: 'Earnings & Work' }]}>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumb={[{ label: 'Earnings & Work' }]}>
      <div className="space-y-5" data-testid="earnings-page">

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-testid="earnings-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${
                tab === t.key
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== DASHBOARD ===== */}
        {tab === 'dashboard' && dash && (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'This Month', value: fmt(dash.month_earned), icon: CalendarDays, color: 'text-teal-600' },
                { label: 'Financial Year', value: fmt(dash.fy_earned), icon: TrendingUp, color: 'text-emerald-600' },
                { label: 'Expected (Unpaid)', value: fmt(dash.total_unpaid), icon: Clock, color: 'text-amber-600' },
                { label: 'Total Paid', value: fmt(dash.total_paid), icon: CheckCircle, color: 'text-emerald-600' },
                { label: 'Shifts Worked', value: dash.total_shifts, icon: Building2, color: 'text-slate-600' },
                { label: 'Outstanding', value: dash.outstanding_count, icon: AlertTriangle, color: 'text-rose-500' },
              ].map((s, i) => (
                <Card key={i} className="bg-white border border-slate-200 shadow-soft" data-testid={`stat-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <s.icon className={`w-4 h-4 ${s.color}`} />
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{s.label}</span>
                    </div>
                    <p className="text-xl font-heading font-bold text-slate-900">{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Add Shift', icon: Plus, color: 'bg-teal-600 hover:bg-teal-700', action: () => setShiftDialog(true) },
                { label: 'Record Payment', icon: DollarSign, color: 'bg-emerald-600 hover:bg-emerald-700', action: () => setPayDialog(true) },
                { label: 'Add Expense', icon: Receipt, color: 'bg-amber-600 hover:bg-amber-700', action: () => setExpenseDialog(true) },
                { label: 'Export Report', icon: Download, color: 'bg-slate-700 hover:bg-slate-800', action: () => setTab('reports') },
              ].map((a, i) => (
                <button
                  key={i}
                  onClick={a.action}
                  className={`${a.color} text-white rounded-xl p-4 flex flex-col items-center gap-2 transition-colors shadow-sm`}
                  data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <a.icon className="w-6 h-6" />
                  <span className="text-xs font-semibold">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ===== SHIFTS ===== */}
        {tab === 'shifts' && (
          <div className="space-y-3 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-heading font-semibold text-slate-800">Shifts</h2>
              <Button size="sm" onClick={() => setShiftDialog(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs" data-testid="add-shift-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Shift
              </Button>
            </div>
            {shifts.length === 0 ? (
              <div className="text-center py-12"><Clock className="w-10 h-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">No shifts logged yet</p></div>
            ) : (
              <div className="space-y-2">
                {shifts.map(s => (
                  <Card key={s.shift_id} className="bg-white border border-slate-200" data-testid={`shift-${s.shift_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-slate-800">{s.school_name}</span>
                          <StatusBadge status={s.status} />
                        </div>
                        <p className="text-xs text-slate-500">{s.date} &middot; {s.session_type} &middot; {s.pay_source}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-slate-900">{fmt(s.expected_pay)}</p>
                        <p className="text-[10px] text-slate-400">Due: {s.expected_pay_date}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {s.status !== 'paid' && (
                          <button onClick={() => handleMarkPaid(s.shift_id)} className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-500" title="Mark paid" data-testid={`pay-${s.shift_id}`}>
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => handleDeleteShift(s.shift_id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-300 hover:text-rose-500" data-testid={`del-shift-${s.shift_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== PAYMENTS ===== */}
        {tab === 'payments' && (
          <div className="space-y-3 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-heading font-semibold text-slate-800">Payments</h2>
              <Button size="sm" onClick={() => setPayDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs" data-testid="record-payment-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Record Payment
              </Button>
            </div>
            {payments.length === 0 ? (
              <div className="text-center py-12"><DollarSign className="w-10 h-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">No payments recorded</p></div>
            ) : (
              <div className="space-y-2">
                {payments.map(p => (
                  <Card key={p.payment_id} className="bg-white border border-slate-200" data-testid={`payment-${p.payment_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{fmt(p.amount)}</p>
                        <p className="text-xs text-slate-500">{p.payment_date} &middot; {p.pay_source}</p>
                        {p.discrepancy !== 0 && <p className="text-xs text-amber-600 font-medium">Discrepancy: {fmt(p.discrepancy)}</p>}
                      </div>
                      <button onClick={async () => {
                        await fetch(`${API}/earnings/payments/${p.payment_id}`, { method: 'DELETE', credentials: 'include' });
                        toast.success('Payment deleted'); fetchAll();
                      }} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-300 hover:text-rose-500" data-testid={`del-pay-${p.payment_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== EXPENSES ===== */}
        {tab === 'expenses' && (
          <div className="space-y-3 animate-fade-in">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-heading font-semibold text-slate-800">Expenses</h2>
              <Button size="sm" onClick={() => setExpenseDialog(true)} className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs" data-testid="add-expense-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
              </Button>
            </div>
            {/* Totals */}
            {dash && (
              <div className="flex gap-3">
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-2 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">This Month</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(dash.month_expenses)}</p>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-2 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">Financial Year</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(dash.fy_expenses)}</p>
                </div>
              </div>
            )}
            {expenses.length === 0 ? (
              <div className="text-center py-12"><Receipt className="w-10 h-10 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">No expenses logged</p></div>
            ) : (
              <div className="space-y-2">
                {expenses.map(e => (
                  <Card key={e.expense_id} className="bg-white border border-slate-200" data-testid={`expense-${e.expense_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        {e.category === 'Travel (km)' ? <Car className="w-4 h-4 text-amber-600" /> : <Receipt className="w-4 h-4 text-amber-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{e.category}</p>
                        <p className="text-xs text-slate-500">
                          {e.date}{e.notes && ` — ${e.notes}`}
                          {e.receipt_base64 && <span className="ml-1.5 inline-flex items-center gap-0.5 text-emerald-600"><Image className="w-3 h-3" />receipt</span>}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-slate-900 flex-shrink-0">{fmt(e.amount)}</p>
                      <button onClick={async () => {
                        await fetch(`${API}/earnings/expenses/${e.expense_id}`, { method: 'DELETE', credentials: 'include' });
                        toast.success('Deleted'); fetchAll();
                      }} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-300 hover:text-rose-500" data-testid={`del-exp-${e.expense_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== TAX ===== */}
        {tab === 'tax' && dash && (
          <div className="space-y-4 animate-fade-in max-w-md">
            <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
              <Percent className="w-4 h-4 text-teal-500" /> Tax Estimation
            </h2>
            <Card className="bg-white border border-slate-200 shadow-soft">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Taxable Income (FY)</span>
                  <span className="text-lg font-bold text-slate-900">{fmt(dash.fy_earned - dash.fy_expenses)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Tax Rate</span>
                  <span className="text-lg font-bold text-slate-900">{dash.tax_percentage}%</span>
                </div>
                <div className="border-t border-slate-100 pt-3 flex items-center justify-between">
                  <span className="text-sm text-slate-600">Estimated Tax</span>
                  <span className="text-lg font-bold text-rose-600">{fmt(dash.estimated_tax)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Net Income</span>
                  <span className="text-xl font-bold text-emerald-600">{fmt(dash.net_income)}</span>
                </div>
                <p className="text-[10px] text-slate-400">Adjust tax rate in Settings. This is an estimate only.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== REPORTS ===== */}
        {tab === 'reports' && (
          <div className="space-y-4 animate-fade-in max-w-md">
            <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-teal-500" /> Export Report
            </h2>
            <Card className="bg-white border border-slate-200 shadow-soft">
              <CardContent className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">What to include</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'both', label: 'Pay & Expenses' },
                      { value: 'pay', label: 'Pay Only' },
                      { value: 'expenses', label: 'Expenses Only' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setExportType(opt.value)}
                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-medium transition-all ${
                          exportType === opt.value
                            ? 'bg-teal-600 text-white shadow-sm'
                            : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`export-type-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">From</label>
                    <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="rounded-xl border-slate-200 text-sm" data-testid="export-from" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">To</label>
                    <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="rounded-xl border-slate-200 text-sm" data-testid="export-to" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={handleExportCSV} variant="outline" className="rounded-xl border-slate-200 text-sm" data-testid="export-csv-btn">
                    <Download className="w-4 h-4 mr-2" /> CSV
                  </Button>
                  <Button onClick={handleExportPDF} variant="outline" className="rounded-xl border-slate-200 text-sm" data-testid="export-pdf-btn">
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== SETTINGS ===== */}
        {tab === 'settings' && (
          <div className="space-y-4 animate-fade-in max-w-md">
            <h2 className="text-sm font-heading font-semibold text-slate-800 flex items-center gap-2">
              <Settings className="w-4 h-4 text-teal-500" /> Pay Rate & Cycle Settings
            </h2>
            <Card className="bg-white border border-slate-200 shadow-soft">
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Full Day</label>
                    <Input type="number" value={settingsForm.default_full_day_rate || ''} onChange={(e) => setSettingsForm(p => ({ ...p, default_full_day_rate: parseFloat(e.target.value) || 0 }))} className="rounded-xl border-slate-200 text-sm" data-testid="rate-full" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Half Day</label>
                    <Input type="number" value={settingsForm.default_half_day_rate || ''} onChange={(e) => setSettingsForm(p => ({ ...p, default_half_day_rate: parseFloat(e.target.value) || 0 }))} className="rounded-xl border-slate-200 text-sm" data-testid="rate-half" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Hourly</label>
                    <Input type="number" value={settingsForm.default_hourly_rate || ''} onChange={(e) => setSettingsForm(p => ({ ...p, default_hourly_rate: parseFloat(e.target.value) || 0 }))} className="rounded-xl border-slate-200 text-sm" data-testid="rate-hourly" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Pay Frequency</label>
                    <Select value={settingsForm.pay_frequency || 'fortnightly'} onValueChange={(v) => setSettingsForm(p => ({ ...p, pay_frequency: v }))}>
                      <SelectTrigger className="rounded-xl border-slate-200 text-sm" data-testid="pay-freq"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="fortnightly">Fortnightly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Pay Delay (days)</label>
                    <Input type="number" value={settingsForm.pay_delay_days || ''} onChange={(e) => setSettingsForm(p => ({ ...p, pay_delay_days: parseInt(e.target.value) || 0 }))} className="rounded-xl border-slate-200 text-sm" data-testid="pay-delay" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Tax Rate (%)</label>
                  <Input type="number" value={settingsForm.tax_percentage || ''} onChange={(e) => setSettingsForm(p => ({ ...p, tax_percentage: parseFloat(e.target.value) || 0 }))} className="rounded-xl border-slate-200 text-sm" data-testid="tax-rate" />
                </div>
                <Button onClick={handleSaveSettings} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm" data-testid="save-settings-btn">
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== DIALOGS ===== */}

        {/* Add Shift Dialog */}
        <Dialog open={shiftDialog} onOpenChange={setShiftDialog}>
          <DialogContent className="bg-white max-w-md rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading text-lg text-slate-900">Add Shift</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">School</label>
                <Select value={shiftForm.school_name} onValueChange={(v) => setShiftForm(p => ({ ...p, school_name: v }))}>
                  <SelectTrigger className="rounded-xl border-slate-200 text-sm" data-testid="shift-school"><SelectValue placeholder="Select school..." /></SelectTrigger>
                  <SelectContent>
                    {schools.map(s => <SelectItem key={s.school_id} value={s.name}>{s.name}</SelectItem>)}
                    <SelectItem value="__other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {shiftForm.school_name === '__other' && (
                  <Input placeholder="School name" className="mt-2 rounded-xl border-slate-200 text-sm" onChange={(e) => setShiftForm(p => ({ ...p, school_name: e.target.value }))} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Date</label>
                  <Input type="date" value={shiftForm.date} onChange={(e) => setShiftForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="shift-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Pay Source</label>
                  <Select value={shiftForm.pay_source} onValueChange={(v) => setShiftForm(p => ({ ...p, pay_source: v }))}>
                    <SelectTrigger className="rounded-xl border-slate-200 text-sm" data-testid="shift-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Session Type</label>
                  <div className="flex gap-1">
                    {SESSION_TYPES.map(t => (
                      <button key={t} onClick={() => handleSessionTypeChange(t)}
                        className={`flex-1 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${shiftForm.session_type === t ? 'bg-teal-100 border-teal-300 text-teal-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                        data-testid={`session-${t.toLowerCase().replace(/\s/g, '-')}`}
                      >{t}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Rate ($)</label>
                  <Input type="number" value={shiftForm.rate} onChange={(e) => setShiftForm(p => ({ ...p, rate: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="shift-rate" />
                </div>
              </div>
              {shiftForm.session_type === 'Hourly' && (
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Hours</label>
                  <Input type="number" value={shiftForm.hours} onChange={(e) => setShiftForm(p => ({ ...p, hours: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="shift-hours" />
                </div>
              )}
              <div className="bg-teal-50 rounded-xl p-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-teal-500 font-semibold">Expected Pay</p>
                <p className="text-xl font-bold text-teal-700">{fmt(calcExpectedPay())}</p>
              </div>
              <Input placeholder="Notes (optional)" value={shiftForm.notes} onChange={(e) => setShiftForm(p => ({ ...p, notes: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="shift-notes" />
              <Button onClick={handleAddShift} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl" data-testid="save-shift-btn">Add Shift</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Record Payment Dialog */}
        <Dialog open={payDialog} onOpenChange={setPayDialog}>
          <DialogContent className="bg-white max-w-md rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading text-lg text-slate-900">Record Payment</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Amount ($)</label>
                  <Input type="number" value={payForm.amount} onChange={(e) => setPayForm(p => ({ ...p, amount: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="pay-amount" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Pay Source</label>
                  <Select value={payForm.pay_source} onValueChange={(v) => setPayForm(p => ({ ...p, pay_source: v }))}>
                    <SelectTrigger className="rounded-xl border-slate-200 text-sm" data-testid="pay-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Payment Date</label>
                <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm(p => ({ ...p, payment_date: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="pay-date" />
              </div>
              {/* Match to unpaid shifts */}
              {unpaidShifts.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Match to unpaid shifts</label>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {unpaidShifts.map(s => (
                      <label key={s.shift_id} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${payForm.shift_ids.includes(s.shift_id) ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100'}`}>
                        <input
                          type="checkbox"
                          checked={payForm.shift_ids.includes(s.shift_id)}
                          onChange={(e) => {
                            setPayForm(p => ({
                              ...p,
                              shift_ids: e.target.checked
                                ? [...p.shift_ids, s.shift_id]
                                : p.shift_ids.filter(id => id !== s.shift_id)
                            }));
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-xs text-slate-700 flex-1">{s.date} — {s.school_name}</span>
                        <span className="text-xs font-semibold text-slate-800">{fmt(s.expected_pay)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Input placeholder="Notes (optional)" value={payForm.notes} onChange={(e) => setPayForm(p => ({ ...p, notes: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="pay-notes" />
              <Button onClick={handleRecordPayment} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-payment-btn">Record Payment</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Expense Dialog */}
        <Dialog open={expenseDialog} onOpenChange={setExpenseDialog}>
          <DialogContent className="bg-white max-w-sm rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading text-lg text-slate-900">Add Expense</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Date</label>
                  <Input type="date" value={expForm.date} onChange={(e) => setExpForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="exp-date" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Amount ($)</label>
                  <Input type="number" value={expForm.amount} onChange={(e) => setExpForm(p => ({ ...p, amount: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="exp-amount" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
                <div className="flex flex-wrap gap-1.5">
                  {EXPENSE_CATEGORIES.map(c => (
                    <button key={c} onClick={() => setExpForm(p => ({ ...p, category: c }))}
                      className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${expForm.category === c ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`}
                      data-testid={`exp-cat-${c.toLowerCase().replace(/[\s()]/g, '-')}`}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <Input placeholder="Notes (optional)" value={expForm.notes} onChange={(e) => setExpForm(p => ({ ...p, notes: e.target.value }))} className="rounded-xl border-slate-200 text-sm" data-testid="exp-notes" />
              
              {/* Receipt photo upload */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Receipt / Proof (optional)</label>
                <input
                  ref={receiptRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleReceiptUpload}
                  data-testid="receipt-file-input"
                />
                {receiptPreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200" data-testid="receipt-preview">
                    <img src={receiptPreview} alt="Receipt" className="w-full h-32 object-cover" />
                    <button
                      onClick={clearReceipt}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-500 transition-colors"
                      data-testid="remove-receipt-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => receiptRef.current?.click()}
                    className="w-full h-24 rounded-xl border-2 border-dashed border-slate-200 hover:border-amber-300 hover:bg-amber-50/30 transition-colors flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-amber-600"
                    data-testid="upload-receipt-btn"
                  >
                    <Camera className="w-5 h-5" />
                    <span className="text-xs font-medium">Tap to take photo or upload</span>
                  </button>
                )}
              </div>

              <Button onClick={handleAddExpense} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-xl" data-testid="save-expense-btn">Add Expense</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
};
